import { useEffect,useState } from 'react';

/**
 * Maximum number of items to keep in the recently viewed list
 */
const MAX_RECENT_ITEMS = 20;

/**
 * Type for items that can be tracked as recently viewed
 */
export interface RecentlyViewedItem {
  id: number;
  name: string;
  timestamp: number;
}

/**
 * Hook to track and manage recently viewed items
 * @param storageKey The localStorage key to use for storing the items
 */
export function useRecentlyViewed(storageKey: string) {
  const [recentItems, setRecentItems] = useState<RecentlyViewedItem[]>([]);

  // Load recently viewed items from localStorage on component mount
  useEffect(() => {
    try {
      const storedItems = localStorage.getItem(storageKey);
      if (storedItems) {
        setRecentItems(JSON.parse(storedItems));
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  /**
   * Add an item to the recently viewed list
   * @param id Item ID
   * @param name Item name
   */
  const addRecentItem = (id: number, name: string) => {
    setRecentItems(prevItems => {
      // Remove the item if it already exists
      const filteredItems = prevItems.filter(item => item.id !== id);
      
      // Add the item to the beginning of the list with current timestamp
      const newItems = [
        { id, name, timestamp: Date.now() },
        ...filteredItems
      ].slice(0, MAX_RECENT_ITEMS); // Keep only the most recent items
      
      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(newItems));
      
      return newItems;
    });
  };

  /**
   * Clear all recently viewed items
   */
  const clearRecentItems = () => {
    localStorage.removeItem(storageKey);
    setRecentItems([]);
  };

  /**
   * Check if an item is in the recently viewed list
   * @param id Item ID
   */
  const isRecentlyViewed = (id: number) => recentItems.some(item => item.id === id);

  return {
    recentItems,
    addRecentItem,
    clearRecentItems,
    isRecentlyViewed
  };
}
